import { Component, ElementRef, Input, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ProjectService } from '../../services/project.service';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';
import { FormLayoutService } from './form-layout.service';
import { FormSettingsDTO } from '../../types/FormSettingsDTO';
import { UrlParamsService } from '../../services/url-params.service';
import { SanitizedHtmlComponent } from '../sanitized-html/sanitized-html.component';
import { GTranslateWidgetService } from '../../services/gtranslate-widget.service';

@Component({
  selector: 'app-form-layout',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, SanitizedHtmlComponent],
  templateUrl: './form-layout.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './form-layout.component.scss',
})
export class FormLayoutComponent {
  @Input() showProjectDetails: boolean = true;
  @ViewChild('gtWrapper') gtWrapper?: ElementRef<HTMLDivElement>;

  private viewReady = false;
  private widgetInitialized = false;
  private initializedLanguages: string[] = [];

  formSettings: FormSettingsDTO = {
    ForeignLanguages: '',
    ProjectFooter: '',
    ProjectLogoUrl: '',
    EntityLogoUrl: '',
    BackgroundImageUrl: '/assets/images/default-background.jpeg',
    ShowEntityLogo: false,
    ShowProjectLogo: false,
    EntityName: '',
    BrandingColor: '',
  };

  backgroundImageUrl: string = this.formSettings.BackgroundImageUrl;
  subscription: Subscription;
  projectGuid: string | null = null;
  meetingGuid: string | null = null;
  projectLogoAltText: null | string = null;

  constructor(
    private projectService: ProjectService,
    private formLayoutService: FormLayoutService,
    private urlParamsService: UrlParamsService,
    private gtranslate: GTranslateWidgetService
  ) {
    this.urlParamsService.projectGuid$.subscribe((guid) => {
      if (guid) this.projectGuid = guid;
    });
    this.urlParamsService.meetingGuid$.subscribe((guid) => {
      if (guid) this.meetingGuid = guid;
    });

    this.subscription = this.formLayoutService.getFormLayoutSettings().subscribe({
      next: (settings) => {
        this.formSettings = settings;
        this.setBackgroundImage(settings.BackgroundImageUrl);

        if (settings.BrandingColor) {
          this.projectService.setBrandingColor(settings.BrandingColor);
        }

        this.tryInitTranslateWidget();
      },
    });
  }

  ngOnInit(): void {
    if (this.projectGuid) {
      this.projectService.getProjectData(this.projectGuid).subscribe({
        next: (data) => {
          this.projectLogoAltText = data?.ProjectName ? data.ProjectName + ' Logo' : null;
        },
        error: (error) => {
          console.error('Error fetching project data:', error);
        },
      });
    } else if (this.meetingGuid) {
      this.projectService.getMeetingData(this.meetingGuid).subscribe({
        next: (data) => {
          this.projectLogoAltText = data?.ProjectName ? data.ProjectName + ' Logo' : null;
        },
        error: (error) => {
          console.error('Error fetching meeting data:', error);
        },
      });
    } else {
      this.projectLogoAltText = 'Project Logo';
    }
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.tryInitTranslateWidget();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private async tryInitTranslateWidget(): Promise<void> {
    try {
      const state = await this.gtranslate.initForWrapper({
        viewReady: this.viewReady,
        wrapper: this.gtWrapper?.nativeElement,
        foreignLanguages: this.formSettings.ForeignLanguages,
        widgetInitialized: this.widgetInitialized,
        initializedLanguages: this.initializedLanguages,
      });

      this.widgetInitialized = state.widgetInitialized;
      this.initializedLanguages = state.initializedLanguages;
    } catch (err) {
      console.error('[GTranslate] init failed:', err);
    }
  }

  private setBackgroundImage(url: string | undefined) {
    if (url) {
      const img = new Image();
      img.src = url;
      img.onload = () => {
        this.backgroundImageUrl = url;
      };
      img.onerror = () => {
        // background image failed to load, switch to use default
        // this happens if the image is no longer available within the s3 bucket
        // rare instance, but in the developers case of copying databases over to template pima, that image may not be available
        this.backgroundImageUrl = '/assets/images/default-background.jpeg';
      };
    } else {
      this.backgroundImageUrl = '/assets/images/default-background.jpeg';
    }
  }
}
